import { useEffect, useState, useRef } from "react";
import FormContainer from "../../containers/FormContainer";
import { createOrganization, uploadFile } from "../../services/apiService";
import SubmitButton from "../shared/SubmitButton";
import TextBox from "../shared/TextBox";
import FileUploader from "../shared/FileUploader";
import SelectBox from "../shared/SelectBox";
import { useTranslation } from "react-i18next";
import createSlug from "../helpers/CreateSlug";
import { organizationSizeOptions, currencyOptions, countryOptions, countryTimezones, industryOptions, languageOptions } from "../../constants";
import { useModal } from "../modal/ModalContext";
import { toast } from "react-toastify";

const CreateOrganization = () => {
    const { t } = useTranslation();
    const scrollRef = useRef(null);
    const { hideModal } = useModal();

    const [isFormLoading, setIsFormLoading] = useState(false);
    const [isFormDisabled, setIsFormDisabled] = useState(true);
    const [formError, setFormError] = useState(null);
    const [selectedLogo, setSelectedLogo] = useState(null);
    const [timezoneOptions, setTimezoneOptions] = useState([]);
    const [addOrgForm, setAddOrgForm] = useState({
        "name": "",
        "slug": "",
        "domain": "",
        "logo": "",
        "website": "",
        "industry": "",
        "size": "",
        "country": "",
        "timezone": "",
        "language": "",
        "currency": "",
        "otherIndustry": ""
    });

    const translatedCurrencyOptions = currencyOptions.map(option => ({
        ...option,
        label: t(option.label)
    }));

    const translatedCountryOptions = countryOptions.map(option => ({
        ...option,
        label: t(option.label)
    }));

    const translatedIndustryOptions = industryOptions.map(option => ({
        ...option,
        label: t(option.label)
    }));

    const translatedLanguageOptions = languageOptions.map(option => ({
        ...option,
        label: t(option.label)
    }));

    useEffect(() => {
        if (addOrgForm.country && countryTimezones[addOrgForm.country]) {
            const options = countryTimezones[addOrgForm.country].map(tz => ({
                ...tz,
                label: t(tz.label)
            }));
            setTimezoneOptions(options);
        } else {
            setTimezoneOptions([]);
        }
    }, [addOrgForm.country, t]);

    useEffect(() => {
        if (formError && scrollRef.current) {
            scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [formError]);

    const handleCreateOrganization = async () => {
        setIsFormLoading(true);
        setFormError(null);
        let updatedForm = { ...addOrgForm };

        if (updatedForm.website && !/^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/i.test(updatedForm.website)) {
             setFormError({ website: t('invalid_url') });
             setIsFormLoading(false);
             return;
        }

        if (updatedForm.domain && !/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i.test(updatedForm.domain)) {
             setFormError({ domain: t('invalid_domain') });
             setIsFormLoading(false);
             return;
        }

        if (updatedForm.industry === 'other' && updatedForm.otherIndustry) {
            updatedForm.industry = updatedForm.otherIndustry;
        }
        delete updatedForm.otherIndustry;

        if (selectedLogo) {
             const { data, error } = await uploadFile(selectedLogo, () => {}, (d) => {}, (e) => {});
             
             if (error) {
                 setFormError(t('upload_failed') || "Failed to upload logo");
                 setIsFormLoading(false);
                 return;
             }
             
             if (data && data.url) {
                 updatedForm.logo = 'https://api-stage.abmfy.com/'+data.filename;
             }
        }

        const { data } = await createOrganization(updatedForm, setIsFormLoading, setAddOrgForm, setFormError);
        
        if (data) {
            toast.success(t('create_org_success'));
            hideModal();
        }
    }

    const handleTypeName = (e) => {
        setAddOrgForm({ ...addOrgForm, name: e.target.value, slug: createSlug(e.target.value) });
    }

    const handleChange = (e) => {
        setAddOrgForm({ ...addOrgForm, [e.target.name]: e.target.value });
    }

    useEffect(()=>{
        if(addOrgForm.name && addOrgForm.slug && typeof addOrgForm.name === 'string' && typeof addOrgForm.slug === 'string' && addOrgForm.name.length > 2 && addOrgForm.slug.length > 2) {
            setIsFormDisabled(false);
        } else {
            setIsFormDisabled(true);
        }
    },[addOrgForm])

    return (
        <div ref={scrollRef} className="p-[40px] max-h-[80vh] overflow-y-auto">
            <FormContainer isLoading={isFormLoading} error={typeof formError === 'string' || Array.isArray(formError) ? formError : (formError ? t('fix_form_errors') : null)} onSubmit={handleCreateOrganization}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextBox label={t('org_name')} value={addOrgForm.name} onChange={(e) => handleTypeName(e)} placeholder={t('org_name_placeholder')} error={formError?.name} />
                    
                    <SelectBox
                        label={t('org_size')}
                        name="size"
                        value={addOrgForm.size}
                        onChange={handleChange}
                        options={organizationSizeOptions}
                        placeholder={t('select_size')}
                    />

                    <TextBox label={t('org_domain')} name="domain" value={addOrgForm.domain} onChange={handleChange} placeholder={t('org_domain_placeholder')} error={formError?.domain} />
                    <TextBox label={t('org_website')} name="website" value={addOrgForm.website} onChange={handleChange} placeholder={t('org_website_placeholder')} error={formError?.website} />
                    
                    <div className="col-span-1 md:col-span-2">
                        <FileUploader 
                            label={t('org_logo')} 
                            limit={1} 
                            onFilesSelected={(files) => setSelectedLogo(files[0])} 
                        />
                    </div>

                    <SelectBox label={t('org_industry')} name="industry" value={addOrgForm.industry} onChange={handleChange} options={translatedIndustryOptions} placeholder={t('org_industry_placeholder')} error={formError?.industry} />
                    {addOrgForm.industry === 'other' && (
                        <TextBox label={t('org_industry_other')} name="otherIndustry" value={addOrgForm.otherIndustry} onChange={handleChange} placeholder={t('org_industry_other_placeholder')} error={formError?.industry} />
                    )}
                    <SelectBox label={t('org_country')} name="country" value={addOrgForm.country} onChange={handleChange} options={translatedCountryOptions} placeholder={t('org_country_placeholder')} error={formError?.country} />
                    <SelectBox 
                        label={t('org_timezone')} 
                        name="timezone" 
                        value={addOrgForm.timezone} 
                        onChange={handleChange} 
                        options={timezoneOptions} 
                        placeholder={t('org_timezone_placeholder')} 
                        error={formError?.timezone} 
                        disabled={!addOrgForm.country || timezoneOptions.length === 0}
                    />
                    <SelectBox label={t('org_language')} name="language" value={addOrgForm.language} onChange={handleChange} options={translatedLanguageOptions} placeholder={t('org_language_placeholder')} error={formError?.language} />
                    <SelectBox label={t('org_currency')} name="currency" value={addOrgForm.currency} onChange={handleChange} options={translatedCurrencyOptions} placeholder={t('org_currency_placeholder')} error={formError?.currency} />
                </div>
                
                <SubmitButton customStyle="inline" label={t('createOrg')} disabled={isFormDisabled} isLoading={isFormLoading} />
            </FormContainer>
        </div>
    );
};

export default CreateOrganization;